import { GridStateKey, UserSettings, ViewType } from '../../types';
import { userSettingsQuery, saveViewType } from '../api/users-api';
import { useState } from 'react';
import { useUserSettings } from '../utils/use-user-settings';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GridStateCreateDialog from './grid-state-create-dialog-latest';
import { getColor } from './color-utils';
import { useGridApiContext, GridInitialState } from '@mui/x-data-grid-pro';
import { useStore } from '../components/state/zustand-store';
import { handleErrors } from '../utils/errors';
import { Messages } from '../utils/message-constants';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Add from '@mui/icons-material/Add';
import CollectionsBookmarkOutlined from '@mui/icons-material/CollectionsBookmarkOutlined';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';

interface SharedGridStateIconButtonProps {
  gridStateKey: GridStateKey;
  module: string;
  section: string;
}

export default function SharedGridStateIconButton({
  gridStateKey,
  module,
  section,
}: SharedGridStateIconButtonProps) {
  const queryClient = useQueryClient();
  const { viewType } = useUserSettings();
  const apiRef = useGridApiContext();
  const { setSnackbar } = useStore();
  const theme = useTheme();
  const [hoveringView, setHoveringView] = useState<string | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const shade = theme.palette.mode === 'light' ? 300 : 500;

  const DEFAULT_VIEW = 'Default';
  const activeGridState =
    viewType[gridStateKey]?.activeGridState || DEFAULT_VIEW;

  const savedGridStates = viewType[gridStateKey]?.savedGridStates || [];
  const savedViews = [
    ...savedGridStates.sort((a, b) => {
      return b.createdDate - a.createdDate;
    }),
    {
      name: DEFAULT_VIEW,
      createdDate: Date.now(),
      color: 'grey',
      value: undefined,
    },
  ];

  const currentActiveView = savedViews.find(
    (view) => view.name === activeGridState
  );

  // including default
  const MAX_SAVED_VIEWS = 6;
  const reachedMaxSavedViews = savedViews.length >= MAX_SAVED_VIEWS;
  const isDefaultView = currentActiveView?.name === DEFAULT_VIEW;

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ViewType) => saveViewType(data),
    onSuccess: (_returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Grid view'),
      });

      queryClient.setQueryData<UserSettings | undefined>(
        userSettingsQuery().queryKey,
        (old) => {
          if (!old || !old?.viewType) return undefined;
          return {
            ...old,
            viewType: {
              ...old.viewType,
              [gridStateKey]: {
                ...old.viewType[gridStateKey],
                savedGridStates: data[gridStateKey]?.savedGridStates,
              },
            },
          };
        }
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('this grid view'),
      });
    },
  });

  function handleUpdateActiveView() {
    if (!currentActiveView) return;
    const currentState: GridInitialState = apiRef.current.exportState();

    const stateToSave: GridInitialState = { ...currentState };
    if (stateToSave.pagination?.paginationModel) {
      stateToSave.pagination = {
        ...stateToSave.pagination,
        paginationModel: {
          ...stateToSave.pagination.paginationModel,
          page: 0,
        },
      };
    }

    const newGridStates = viewType[gridStateKey]?.savedGridStates.map(
      (view) => {
        if (view.name === currentActiveView.name) {
          return { ...view, value: JSON.stringify(stateToSave) };
        }
        return view;
      }
    );

    mutate({
      ...viewType,
      [gridStateKey]: {
        ...viewType[gridStateKey],
        savedGridStates: newGridStates,
      },
    });
  }

  return (
    <>
      <Tooltip arrow title="Save Grid View">
        <IconButton
          id="saved-states-button"
          onClick={handleClick}
          aria-controls={open ? 'saved-states-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge
            variant="dot"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiBadge-dot': {
                backgroundColor: getColor(currentActiveView?.color || 'grey')[
                  shade
                ],
              },
            }}
          >
            <CollectionsBookmarkOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        id="saved-states-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <Box sx={{ width: 275 }}>
          <ListSubheader
            sx={{
              lineHeight: 2.5,
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'transparent',
            }}
          >
            Saved Grid Views
          </ListSubheader>
          {savedViews.map((view, index) => (
            <MenuItem
              selected={view.name === activeGridState}
              key={view.name}
              onClick={() => {
                saveViewType({
                  ...viewType,
                  [gridStateKey]: {
                    ...viewType[gridStateKey],
                    activeGridState: view.name,
                  },
                });

                queryClient.setQueryData<UserSettings | undefined>(
                  userSettingsQuery().queryKey,
                  (old) => {
                    if (!old || !old?.viewType) return undefined;

                    return {
                      ...old,
                      viewType: {
                        ...old.viewType,
                        [gridStateKey]: {
                          ...old.viewType[gridStateKey],
                          activeGridState: view.name,
                        },
                      },
                    };
                  }
                );

                handleClose();
              }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                height: 42,
                pr: 0.5,
                '&.Mui-selected': {
                  backgroundColor: (theme) => theme.palette.primary.light,
                },
              }}
              autoFocus={index === 0}
              onMouseEnter={() => setHoveringView(view.name)}
              onMouseLeave={() => setHoveringView(null)}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ width: '73%' }}
              >
                <Box
                  sx={{
                    backgroundColor: getColor(view.color)[shade],
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {view.name}
                </Typography>
              </Stack>
              {hoveringView === view.name && view.name !== DEFAULT_VIEW ? (
                <Stack direction="row" alignItems="center" gap={0.25}>
                  {view.name === viewType[gridStateKey]?.activeGridState &&
                  isPending ? (
                    <CircularProgress size={16} />
                  ) : view.name === viewType[gridStateKey]?.activeGridState ? (
                    <Tooltip
                      title={
                        isDefaultView
                          ? "Default view can't be updated"
                          : 'Update Active View'
                      }
                      arrow
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateActiveView();
                          }}
                          disabled={isDefaultView}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Delete View" arrow placement="top">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();

                        const newViewType: ViewType = {
                          ...viewType,
                          [gridStateKey]: {
                            ...viewType[gridStateKey],
                            // if the active grid state is the hovering view being deleted, set it to the default view
                            activeGridState:
                              viewType[gridStateKey]?.activeGridState ===
                              hoveringView
                                ? DEFAULT_VIEW
                                : viewType[gridStateKey]?.activeGridState,
                            // remove the view from the saved grid states
                            savedGridStates: viewType[
                              gridStateKey
                            ]?.savedGridStates.filter(
                              (state) => state.name !== hoveringView
                            ),
                          },
                        };
                        saveViewType(newViewType);

                        queryClient.setQueryData<UserSettings | undefined>(
                          userSettingsQuery().queryKey,
                          (old) => {
                            if (!old || !old?.viewType) return undefined;

                            return {
                              ...old,
                              viewType: newViewType,
                            };
                          }
                        );
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Box width={100} />
              )}
            </MenuItem>
          ))}
          <Divider />
          <Tooltip
            title={
              reachedMaxSavedViews
                ? 'You have reached the maximum number of saved views. Delete a view to save a new one.'
                : ''
            }
            arrow
            placement="bottom"
          >
            <span>
              <MenuItem
                onClick={() => {
                  setOpenCreateDialog(true);
                }}
                disabled={reachedMaxSavedViews}
              >
                <ListItemIcon>
                  <Add />
                </ListItemIcon>
                <ListItemText>Save New View</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Box>
      </Menu>
      {openCreateDialog && (
        <GridStateCreateDialog
          open={openCreateDialog}
          handleClose={() => setOpenCreateDialog(false)}
          module={module}
          section={section}
          gridStateKey={gridStateKey}
        />
      )}
    </>
  );
}
